import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import FilterBar from '../components/FilterBar.js';
import BlockedOverlay from '../components/BlockedOverlay.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { useFilters } from '../context/FilterContext.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

import CompetitivenessIndex from '../components/CompetitivenessIndex.js';
import PdfDownloadButton from '../components/PdfDownloadButton.js';

const COLORS = ['#2563eb', '#0f172a', '#64748b', '#94a3b8', '#cbd5e1'];

const PosTag = ({ diff, inverted = false }) => {
  if (diff === null || diff === undefined) return <span className="text-[10px] text-slate-300">--</span>;
  const isGood = inverted ? diff < 0 : diff > 0;
  const isBad = inverted ? diff > 0 : diff < 0;
  const Icon = isGood ? ArrowUp : isBad ? ArrowDown : Minus;
  const cls = isGood ? 'bg-emerald-100 text-emerald-700' : isBad ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{diff > 0 ? '+' : ''}{diff}%
    </span>
  );
};

const ScoreCard = ({ label, score, color = 'blue', myScore, myDiff }) => {
  const colorMap = { blue: 'text-blue-600 bg-blue-50', green: 'text-emerald-600 bg-emerald-50', purple: 'text-violet-600 bg-violet-50', navy: 'text-slate-800 bg-slate-100' };
  return (
    <div className={`rounded-lg p-5 ${colorMap[color] || colorMap.blue}`} data-testid={`score-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
      <p className="text-3xl font-bold">{score?.toFixed(1) || '0'}</p>
      <p className="text-xs opacity-50 mt-1">/ 100</p>
      {myScore !== null && myScore !== undefined && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-current/10">
          <span className="text-[10px] opacity-60">Sua Empresa: <strong>{myScore.toFixed(1)}</strong></span>
          <PosTag diff={myDiff} />
        </div>
      )}
    </div>
  );
};

const MetricRow = ({ label, stat, unit = '%', myVal, myDiff, inverted = false }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-600">{label}</span>
    <div className="flex items-center gap-4">
      <span className="text-sm font-semibold text-slate-900">{stat?.media?.toFixed(1)}{unit}</span>
      <span className="text-xs text-slate-400">1ºQ: {stat?.p25?.toFixed(1)} | 3ºQ: {stat?.p75?.toFixed(1)}</span>
      {myVal !== null && myVal !== undefined && (
        <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-slate-200">
          <span className="text-[10px] text-slate-500">{myVal.toFixed(1)}{unit}</span>
          <PosTag diff={myDiff} inverted={inverted} />
        </div>
      )}
    </div>
  </div>
);

const ESGDashboard = () => {
  const [data, setData] = useState(null);
  const { fetchWithFilters, filters, buildQuery } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  useEffect(() => {
    const load = async () => {
      try {
        const result = await fetchWithFilters('/esg/indicators');
        setData(result);
      } catch (e) { console.error(e); }
    };
    load();
  }, [filters, fetchWithFilters]);

  if (!data) return <MainLayout title="ESG & Pessoas" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  const blocked = data.blocked;
  const me = data.minha_empresa;

  const pieData = [
    { name: 'Ambiental', value: data.scores?.ambiental?.media || 0 },
    { name: 'Social', value: data.scores?.social?.media || 0 },
    { name: 'Governança', value: data.scores?.governanca?.media || 0 },
  ];

  const segmentChart = (data.by_segment || []).map(s => ({
    name: s.label.length > 12 ? s.label.slice(0, 12) + '...' : s.label,
    Geral: s.geral,
    Ambiental: s.ambiental,
    Social: s.social,
  }));

  return (
    <MainLayout title="ESG & Pessoas" userRole={userRole}>
      <div className="space-y-6" data-testid="esg-dashboard">
        <FilterBar n={data.n} blocked={blocked} />

        {blocked ? <BlockedOverlay n={data.n} /> : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CompetitivenessIndex variant="compact" />
              <PdfDownloadButton endpoint="/api/reports/esg/pdf" queryString={buildQuery()} label="PDF ESG & Pessoas" testId="esg-pdf-download" />
            </div>

            {me && (
              <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-lg p-4 text-white" data-testid="esg-company-banner">
                <p className="text-xs opacity-50 mb-1">Posicionamento da sua empresa vs Mercado (ESG)</p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-2">
                  {[
                    { l: 'Ambiental', v: me.score_ambiental, d: me.score_ambiental_diff_pct },
                    { l: 'Social', v: me.score_social, d: me.score_social_diff_pct },
                    { l: 'Governança', v: me.score_governanca, d: me.score_governanca_diff_pct },
                    { l: 'Div. Gênero', v: me.diversidade_genero, d: me.diversidade_genero_diff_pct },
                    { l: 'Lid. Fem.', v: me.lideranca_feminina, d: me.lideranca_feminina_diff_pct },
                    { l: 'Gap Salarial', v: me.equidade_salarial_gap, d: me.equidade_salarial_gap_diff_pct, inv: true },
                  ].filter(i => i.v !== null).map((item, i) => (
                    <div key={i} className="bg-white/10 rounded-lg p-2.5">
                      <p className="text-[10px] opacity-60">{item.l}</p>
                      <p className="text-lg font-bold">{item.v?.toFixed(1)}</p>
                      {item.d !== null && <PosTag diff={item.d} inverted={item.inv} />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <ScoreCard label="Score ESG Geral" score={data.score_esg_geral} color="blue" myScore={me ? ((me.score_ambiental || 0) + (me.score_social || 0) + (me.score_governanca || 0)) / 3 : null} myDiff={null} />
              <ScoreCard label="Ambiental" score={data.scores?.ambiental?.media} color="green" myScore={me?.score_ambiental} myDiff={me?.score_ambiental_diff_pct} />
              <ScoreCard label="Social" score={data.scores?.social?.media} color="purple" myScore={me?.score_social} myDiff={me?.score_social_diff_pct} />
              <ScoreCard label="Governança" score={data.scores?.governanca?.media} color="navy" myScore={me?.score_governanca} myDiff={me?.score_governanca_diff_pct} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Diversidade & Inclusão</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  <MetricRow label="Mulheres no quadro" stat={data.diversidade_genero} myVal={me?.diversidade_genero} myDiff={me?.diversidade_genero_diff_pct} />
                  <MetricRow label="Diversidade racial" stat={data.diversidade_racial} myVal={me?.diversidade_racial} myDiff={me?.diversidade_racial_diff_pct} />
                  <MetricRow label="PCD" stat={data.diversidade_pcd} myVal={me?.diversidade_pcd} myDiff={me?.diversidade_pcd_diff_pct} />
                  <MetricRow label="Liderança feminina" stat={data.lideranca_feminina} myVal={me?.lideranca_feminina} myDiff={me?.lideranca_feminina_diff_pct} />
                  <MetricRow label="Gap salarial gênero" stat={data.equidade_salarial_gap} myVal={me?.equidade_salarial_gap} myDiff={me?.equidade_salarial_gap_diff_pct} inverted />
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Indicadores Ambientais</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  <MetricRow label="Energia renovável" stat={data.energia_renovavel} myVal={me?.energia_renovavel} myDiff={me?.energia_renovavel_diff_pct} />
                  <MetricRow label="Resíduos reciclados" stat={data.residuos_reciclados} myVal={me?.residuos_reciclados} myDiff={me?.residuos_reciclados_diff_pct} />
                  <MetricRow label="Emissões CO2" stat={data.emissoes_co2} unit=" ton" myVal={me?.emissoes_co2} myDiff={me?.emissoes_co2_diff_pct} inverted />
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Composição Score ESG</CardTitle></CardHeader>
                <CardContent className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name}: ${value.toFixed(0)}`}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Score ESG por Segmento</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={segmentChart} margin={{ top: 5, right: 20, bottom: 60, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                      <Tooltip />
                      <Bar dataKey="Geral" fill="#2563eb" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default ESGDashboard;
